import React, { useState, useEffect } from 'react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, 
  Legend as RechartsLegend, ResponsiveContainer,
  ScatterChart, Scatter, ZAxis, Cell, Label
} from 'recharts';
import cloudflareService from '../services/cloudflare';
import { CapeScore } from '../types';

const SimpleRatioChart: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [capeScores, setCapeScores] = useState<CapeScore[]>([]);
  const [selectedView, setSelectedView] = useState<'bar' | 'scatter'>('bar');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [filterValue, setFilterValue] = useState<number>(0);
  const [retryCount, setRetryCount] = useState(0);
  
  // Fetch CAPE scores when component mounts
  useEffect(() => {
    const fetchCapeScores = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const scores = await cloudflareService.getLatestCapeScores();
        
        if (scores && scores.length > 0) {
          // Sort by ticker
          setCapeScores(scores.sort((a, b) => a.ticker.localeCompare(b.ticker)));
        } else {
          setError('No CAPE score data available. Please try again later.');
        }
      } catch (err) {
        console.error('Error fetching CAPE scores:', err);
        setError('Failed to load CAPE score data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchCapeScores();
  }, [retryCount]);
  
  // Retry loading data function
  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
  };
  
  // Handle sorting change
  const handleSortOrderChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSortOrder(e.target.value as 'asc' | 'desc');
  };
  
  // Handle view change
  const handleViewChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedView(e.target.value as 'bar' | 'scatter');
  };
  
  // Handle filter change
  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilterValue(Number(e.target.value));
  };
  
  // Prepare chart data
  const prepareChartData = () => {
    if (!capeScores || capeScores.length === 0) return [];
    
    // Filter out scores with CAPE below the filter value
    const filteredScores = capeScores.filter(score => score.cape >= filterValue);
    
    // Sort based on selected order
    const sortedScores = [...filteredScores].sort((a, b) => {
      if (sortOrder === 'asc') {
        return a.cape - b.cape;
      } else {
        return b.cape - a.cape;
      }
    });
    
    // Return at most 30 scores to keep chart readable
    return sortedScores.slice(0, 30);
  };
  
  // Get color based on CAPE percentile
  const getColor = (percentile: number) => {
    if (percentile <= 20) return '#22c55e'; // Green - undervalued
    if (percentile >= 80) return '#ef4444'; // Red - overvalued
    return '#f59e0b'; // Yellow - neutral
  };
  
  // Prepare scatter chart data
  const prepareScatterData = () => {
    if (!capeScores || capeScores.length === 0) return [];
    
    // Filter out scores with CAPE below the filter value
    return capeScores
      .filter(score => score.cape >= filterValue)
      .map(score => ({
        ticker: score.ticker,
        cape: score.cape,
        percentile: score.capePercentile,
        // Add some jitter to y-coordinate to avoid overlapping points
        y: score.capePercentile + (Math.random() - 0.5) * 2
      }));
  };
  
  const chartData = prepareChartData();
  const scatterData = prepareScatterData();
  
  return (
    <div className="dashboard-card">
      <h6 className="dashboard-section-title">CAPE Ratio Analysis</h6>
      
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading CAPE ratio data...</p>
        </div>
      ) : error ? (
        <div className="alert alert-danger m-3" role="alert">
          <p>{error}</p>
          <div className="mt-3">
            <button 
              onClick={handleRetry} 
              className="btn btn-outline-danger btn-sm"
            >
              <i className="bi bi-arrow-clockwise me-1"></i> Try again later
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="card-body">
            <div className="row mb-4">
              <div className="col-md-3">
                <div className="mb-3">
                  <label htmlFor="chartTypeSelect" className="form-label text-secondary small">Chart Type</label>
                  <select 
                    id="chartTypeSelect"
                    className="form-select form-select-sm bg-dark text-light border-secondary"
                    value={selectedView}
                    onChange={handleViewChange}
                  >
                    <option value="bar">Bar Chart</option>
                    <option value="scatter">Scatter Plot</option>
                  </select>
                </div>
              </div>
              <div className="col-md-3">
                <div className="mb-3">
                  <label htmlFor="sortOrderSelect" className="form-label text-secondary small">Sort Order</label>
                  <select
                    id="sortOrderSelect"
                    className="form-select form-select-sm bg-dark text-light border-secondary"
                    value={sortOrder}
                    onChange={handleSortOrderChange}
                  >
                    <option value="asc">Ascending</option>
                    <option value="desc">Descending</option>
                  </select>
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label htmlFor="capeValueRange" className="form-label text-secondary small">
                    Minimum CAPE Value: <span className="badge bg-primary">{filterValue}</span>
                  </label>
                  <input
                    type="range"
                    className="form-range"
                    id="capeValueRange"
                    min={0}
                    max={50}
                    step={1}
                    value={filterValue}
                    onChange={handleFilterChange}
                  />
                </div>
              </div>
            </div>
            
            <div style={{ width: '100%', height: 400 }}>
              {chartData.length === 0 ? (
                <div className="alert alert-info">
                  No data matches your current filter. Try adjusting the minimum CAPE value or refresh data.
                </div>
              ) : selectedView === 'bar' ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={chartData}
                    margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis 
                      dataKey="ticker" 
                      angle={-45} 
                      textAnchor="end"
                      tick={{ fontSize: 12, fill: '#e2e8f0' }}
                      interval={0}
                      stroke="#334155"
                    />
                    <YAxis 
                      stroke="#334155"
                      tick={{ fill: '#e2e8f0' }}
                    >
                      <Label
                        value="CAPE Ratio"
                        angle={-90}
                        position="insideLeft"
                        style={{ textAnchor: 'middle', fill: '#e2e8f0' }}
                      />
                    </YAxis>
                    <RechartsTooltip 
                      formatter={(value: number, name: string, props: any) => {
                        if (name === 'cape') {
                          return [`${value.toFixed(2)}`, 'CAPE Ratio'];
                        }
                        return [value, name];
                      }}
                      labelFormatter={(label) => `${label}`}
                      contentStyle={{ backgroundColor: '#1e293b', border: 'none', color: '#e2e8f0' }}
                    />
                    <RechartsLegend wrapperStyle={{ color: '#e2e8f0' }} />
                    <Bar
                      dataKey="cape"
                      name="CAPE Ratio"
                      fill="#8884d8"
                    >
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={getColor(entry.capePercentile)} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <ScatterChart
                    margin={{ top: 20, right: 20, bottom: 60, left: 30 }}
                  >
                    <CartesianGrid stroke="rgba(255,255,255,0.1)" />
                    <XAxis 
                      type="number" 
                      dataKey="cape" 
                      name="CAPE Ratio"
                      domain={['auto', 'auto']}
                      tick={{ fill: '#e2e8f0' }}
                      stroke="#334155"
                    >
                      <Label 
                        value="CAPE Ratio" 
                        position="bottom" 
                        offset={20}
                        style={{ fill: '#e2e8f0' }}
                      />
                    </XAxis>
                    <YAxis 
                      type="number" 
                      dataKey="y" 
                      name="Percentile" 
                      domain={[0, 100]}
                      tickCount={6}
                      tick={{ fill: '#e2e8f0' }}
                      stroke="#334155"
                    >
                      <Label 
                        value="Percentile" 
                        angle={-90} 
                        position="insideLeft"
                        style={{ fill: '#e2e8f0' }}
                      />
                    </YAxis>
                    <ZAxis
                      type="category"
                      dataKey="ticker"
                      range={[50, 500]}
                      name="Ticker"
                    />
                    <RechartsTooltip
                      cursor={{ strokeDasharray: '3 3' }}
                      formatter={(value: any, name: string, props: any) => {
                        if (name === 'CAPE Ratio') {
                          return [value.toFixed(2), name];
                        } else if (name === 'Percentile') {
                          // Use the actual percentile, not the jittered value
                          const ticker = props.payload.ticker;
                          const score = capeScores.find(s => s.ticker === ticker);
                          return [score ? score.capePercentile : value, name];
                        }
                        return [value, name];
                      }}
                      labelFormatter={(label) => `${label}`}
                      contentStyle={{ backgroundColor: '#1e293b', border: 'none', color: '#e2e8f0' }}
                    />
                    <Scatter 
                      name="Companies" 
                      data={scatterData} 
                      fill="#8884d8"
                    >
                      {scatterData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={getColor(entry.percentile)}
                        />
                      ))}
                    </Scatter>
                  </ScatterChart>
                </ResponsiveContainer>
              )}
            </div>
            
            <div className="mt-4">
              <p className="text-secondary small mb-2">CAPE Ratio Legend</p>
              <div className="d-flex gap-4">
                <div className="d-flex align-items-center me-3">
                  <div style={{
                    width: 16,
                    height: 16,
                    backgroundColor: '#22c55e',
                    marginRight: 8
                  }}></div>
                  <span className="text-secondary small">Undervalued (0-20th)</span>
                </div>
                <div className="d-flex align-items-center me-3">
                  <div style={{
                    width: 16,
                    height: 16,
                    backgroundColor: '#f59e0b',
                    marginRight: 8
                  }}></div>
                  <span className="text-secondary small">Neutral (20-80th)</span>
                </div>
                <div className="d-flex align-items-center">
                  <div style={{
                    width: 16,
                    height: 16,
                    backgroundColor: '#ef4444',
                    marginRight: 8
                  }}></div>
                  <span className="text-secondary small">Overvalued (80-100th)</span>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SimpleRatioChart; 